import { LocalStorageKey, PATH } from '@/constants'
import { LiveChannelRegion } from '@setplex/tria-api'
import { createEvent, createStore, sample } from 'effector'
import { persist } from 'effector-storage/local'
import { model as router } from '~/core/router'
import { parseEpgPath, updateEpgPath } from '~/entities/epg/lib'
import { epgRegionsModel } from '~/entities/epg/regions'
import { formatTitleForUrl } from '~/shared/helpers'

export const setRegion = createEvent<LiveChannelRegion>()
export const setRegionFromUrl = createEvent<LiveChannelRegion>()
export const setRegionFromPopup = createEvent<LiveChannelRegion | null>()

export const resetRegionStorage = createEvent()
export const $regionStorage = createStore<LiveChannelRegion | null>(null, {
  updateFilter: (update, current) => {
    return update?.id !== current?.id && update?.name !== current?.name
  },
})
  .on([setRegion, setRegionFromUrl], (_, data) => {
    return data
  })
  .reset(resetRegionStorage)

persist({
  store: $regionStorage,
  key: LocalStorageKey.EpgRegion,
})

// EPG page navigations:
// - make seo friendly url: /1 -> /arabic-1
// - correct title: /arab-1 -> /arabic-1
// (also can be another combinations, f.e. /1/drama-2 -> /arabic-1/drama-2)
sample({
  clock: [epgRegionsModel.$liveRegions, $regionStorage],
  source: {
    pathname: router.$pathname,
    activeRegion: $regionStorage,
    regions: epgRegionsModel.$liveRegions,
  },
  filter: ({ pathname, activeRegion, regions }) => {
    const region = regions?.find((r) => r.id === activeRegion?.id)
    // if no title no need to update url
    if (!activeRegion?.id || activeRegion?.id !== region?.id || !region?.name)
      return false

    // Check url region
    const { regionId, regionTitle } = parseEpgPath(pathname ?? '')
    if (!regionId || regionId !== activeRegion?.id) return false

    const existedRegionSegment = `${regionTitle}-${regionId}`
    const correctRegionSegment = `${formatTitleForUrl(region?.name)}${region?.id}`

    return (
      existedRegionSegment !== correctRegionSegment &&
      Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`))
    )
  },
  fn: ({ pathname, activeRegion, regions }) => {
    if (!activeRegion?.id) return pathname

    const region = regions?.find((r) => r.id === activeRegion.id)
    return updateEpgPath(pathname, {
      regionId: region?.id ?? activeRegion.id,
      regionTitle: region?.name ?? activeRegion.name,
    })
  },
  // When user clicks the region row see all button at home page replace the current URL instead of pushing a new one
  // This prevents duplicate entries in browser history and loop when click browser back button
  target: router.navigateReplace,
})

// add region id and name to url (pathname) at the EPG page
sample({
  clock: setRegion,
  source: router.$pathname,
  filter: (pathname, region) => {
    return (
      Boolean(region?.id) &&
      Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`)) &&
      // Skip URL update when on base EPG path (/live/categories) without region
      // This prevents /live/categories from being added to browser history
      // Example: /live/categories -> /live/categories/region-1 (skips adding /live/categories to history)
      pathname !== `${PATH.LIVE}${PATH.CATEGORIES}`
    )
  },
  fn: (pathname, region) => {
    if (!region?.id) return pathname
    return updateEpgPath(pathname, {
      regionId: region.id,
      regionTitle: region.name,
    })
  },
  target: router.navigatePush,
})

// When user clicks the Live module in navbar replace the current URL instead of pushing a new one (from /live/categories to /live/categories/region-1).
// This prevents duplicate entries in browser history and loop when click browser back button
sample({
  clock: setRegion,
  source: router.$pathname,
  filter: (pathname, region) => {
    return Boolean(region?.id) && pathname === `${PATH.LIVE}${PATH.CATEGORIES}`
  },
  fn: (pathname, region) => {
    if (!region?.id) return pathname
    return updateEpgPath(pathname, {
      regionId: region.id,
      regionTitle: region.name,
    })
  },
  target: router.navigateReplace,
})
